import React, { useState } from 'react'
import { Popover, PopoverBody } from 'reactstrap'
import { BsThreeDots } from 'react-icons/bs'
import { BiFolder } from 'react-icons/bi'
import ReactTooltip from 'react-tooltip'
import {
  LayoutRowShadow,
  LayoutData,
} from '../../../components/TableComponents/RowCardShadow/RowCardShadow'
import { formatItemCount } from './folderService'

// A folder rendered as a table row that visually matches the trivvy rows.
// - Native HTML5 drop target (a trivvy row can be dragged onto it).
// - Not draggable itself.
// - Item count lives in the Name column (never in Status).
// - EVERY middle (data) cell — Status, Send Date, Questions, Sent, Responses,
//   … — shows a dash "—" (.list-empty-dash), because none of the trivvy metrics
//   apply to a folder. Callers pass one width per real column (middleColWidths)
//   so each dash sits under its own header (TBV-726). No date is ever shown: the
//   date column is "Send Date" and a folder is never sent. Folders are still
//   ordered newest-first via folderService.sortFolders.
// - `middleColClasses` mirrors the per-column class the TRIVVY row cell uses, so
//   the folder dash inherits the same desktop padding-left nudges (sent-cell 34px,
//   response-rate-cell 44px, scheduled-cell 56px, avg-*-cell) that shift those
//   columns under their wider/wrapped headers. Without it the dashes drift left of
//   the trivvy values, and the drift grows across the row. Pass ONLY alignment
//   classes — not colour/size ones like `waiting_text` (orangered/bold/13px),
//   which would restyle the dash.
const FolderRow = ({
  folder,
  itemCount,
  itemNoun,
  nameColWidth,
  middleColWidths,
  middleColClasses,
  actionColWidth,
  isAdmin,
  isDragOver,
  onOpen,
  onDragOver,
  onDragLeave,
  onDrop,
  onRename,
  onDelete,
}) => {
  const [menuOpen, setMenuOpen] = useState(false)

  const popoverId = `folderListRow_${folder._id}`
  const blanks = middleColWidths || []
  const blankClasses = middleColClasses || []

  const handleDragOver = (e) => {
    // Must preventDefault so the row is a valid drop target.
    e.preventDefault()
    if (onDragOver) {
      onDragOver(e)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    if (onDrop) {
      onDrop(e)
    }
  }

  return (
    <LayoutRowShadow
      className={`folder-row ${isDragOver ? 'folder-row-dragover' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={onDragLeave}
      onDrop={handleDrop}
    >
      <LayoutData colWidth={nameColWidth} className="cursor-pointer trivvy-name-col">
        <div
          className="d-flex trivvy-tbl-list-row-name-col folder-row-name-col"
          onClick={() => {
            if (onOpen) {
              onOpen()
            }
          }}
          data-tip={folder.name}
        >
          <div className="folder-row-icon-wrap">
            <BiFolder className="folder-row-icon" />
          </div>
          <span className="survey-item-title d-block">
            <span className="survey-item-name-text">{folder.name}</span>
            <div className="folder-row-count">
              {formatItemCount(itemCount, itemNoun)}
            </div>
            <ReactTooltip />
          </span>
        </div>
      </LayoutData>

      {blanks.map((width, idx) => (
        <LayoutData
          key={idx}
          colWidth={width}
          className={`text-center ${blankClasses[idx] || ''}`}
        >
          <span className="list-empty-dash">&mdash;</span>
        </LayoutData>
      ))}

      <LayoutData colWidth={actionColWidth} className="text-center">
        {isAdmin ? (
          <div className="cursor-pointer">
            <span
              id={popoverId}
              className="cursor-pointer"
              onClick={() => setMenuOpen(true)}
            >
              <BsThreeDots />
            </span>
            <Popover
              isOpen={menuOpen}
              target={popoverId}
              toggle={() => setMenuOpen(false)}
              placement="bottom"
              modifiers={{
                flip: { boundariesElement: 'viewport' },
                preventOverflow: { boundariesElement: 'viewport' },
              }}
              hideArrow
              className="surveyListRow_Popover"
            >
              <PopoverBody>
                <div
                  className="dropdown-item my-1 cursor-pointer"
                  onClick={() => {
                    setMenuOpen(false)
                    if (onOpen) {
                      onOpen()
                    }
                  }}
                >
                  Open folder
                </div>
                <div
                  className="dropdown-item my-1 cursor-pointer"
                  onClick={() => {
                    setMenuOpen(false)
                    if (onRename) {
                      onRename(folder)
                    }
                  }}
                >
                  Rename folder
                </div>
                <div
                  className="dropdown-item my-1 cursor-pointer folder-menu-item-delete"
                  onClick={() => {
                    setMenuOpen(false)
                    if (onDelete) {
                      onDelete(folder)
                    }
                  }}
                >
                  Delete folder
                </div>
              </PopoverBody>
            </Popover>
          </div>
        ) : null}
      </LayoutData>
    </LayoutRowShadow>
  )
}

export default FolderRow
